import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import Button from "./Button";

export type QuillRange = { index: number; length: number };

export type QuillLike = {
  getSelection: (focus?: boolean) => QuillRange | null;
  getFormat: (...args: any[]) => { link?: string | boolean };
  getText: (index?: number, length?: number) => string;
  getLeaf: (index: number) => [any, number];
  getIndex: (blot: any) => number;
  getLength: () => number;
  getModule: (name: string) => any;
  focus: () => void;
  deleteText: (...args: any[]) => void;
  insertText: (...args: any[]) => void;
  formatText: (...args: any[]) => void;
  setSelection: (...args: any[]) => void;
  theme?: { tooltip?: { edit?: (...args: any[]) => void; hide?: () => void } };
};

export type LinkDialogPayload = {
  quill: QuillLike;
  range: QuillRange;
  text: string;
  url: string;
  isEdit: boolean;
};

type QuillLinkDialogProps = {
  payload: LinkDialogPayload | null;
  onClose: () => void;
};

const hasProtocol = (value: string) => /^[a-z][a-z0-9+.-]*:/i.test(value);

export const normalizeHref = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (hasProtocol(trimmed) || trimmed.startsWith("/")) return trimmed;
  return `https://${trimmed}`;
};

const findLinkBlot = (quill: QuillLike, index: number) => {
  try {
    let [blot] = quill.getLeaf(index);
    while (blot) {
      if (blot.statics?.blotName === "link" || blot.domNode?.tagName === "A") {
        return blot;
      }
      blot = blot.parent;
    }
  } catch {
    // Quill can throw when the index is at the document edge
  }
  return null;
};

const readLinkFromBlot = (blot: any): string => {
  const fromFormats = blot.formats?.()?.link;
  if (typeof fromFormats === "string") return fromFormats;
  return blot.domNode?.getAttribute?.("href") || "";
};

export const getSelectionLinkInfo = (
  quill: QuillLike
): Omit<LinkDialogPayload, "quill"> => {
  const selection = quill.getSelection(true);
  const range = selection || { index: Math.max(quill.getLength() - 1, 0), length: 0 };

  if (range.length === 0) {
    const blot =
      findLinkBlot(quill, range.index) ||
      (range.index > 0 ? findLinkBlot(quill, range.index - 1) : null);

    if (blot) {
      const index = quill.getIndex(blot);
      const length = typeof blot.length === "function" ? blot.length() : 0;
      if (length > 0 && range.index >= index && range.index <= index + length) {
        return {
          range: { index, length },
          text: quill.getText(index, length).replace(/\n+$/, ""),
          url: readLinkFromBlot(blot),
          isEdit: true,
        };
      }
    }
  }

  const formats = range.length
    ? quill.getFormat(range)
    : quill.getFormat(range.index);
  const url = typeof formats.link === "string" ? formats.link : "";
  const text = range.length
    ? quill.getText(range.index, range.length).replace(/\n+$/, "")
    : "";

  return { range, text, url, isEdit: Boolean(url) };
};

export const applyQuillLink = (
  quill: QuillLike,
  range: QuillRange,
  text: string,
  url: string
) => {
  const href = normalizeHref(url);
  if (!href) return;

  const displayText = text.trim() || href;
  const currentText = range.length
    ? quill.getText(range.index, range.length).replace(/\n+$/, "")
    : "";

  quill.focus();

  if (range.length > 0 && currentText === displayText) {
    quill.formatText(range.index, range.length, "link", href, "user");
    quill.setSelection(range.index + range.length, 0, "silent");
    return;
  }

  if (range.length > 0) {
    quill.deleteText(range.index, range.length, "user");
  }

  quill.insertText(range.index, displayText, { link: href }, "user");
  quill.setSelection(range.index + displayText.length, 0, "silent");
};

export const removeQuillLink = (quill: QuillLike, range: QuillRange) => {
  quill.focus();
  if (range.length > 0) {
    quill.formatText(range.index, range.length, "link", false, "user");
  }
  quill.setSelection(range.index, range.length, "silent");
};

export const bindQuillLinkDialog = (
  quill: any,
  openDialog: (payload: LinkDialogPayload) => void
): (() => void) => {
  const toolbar = quill.getModule("toolbar");
  toolbar?.addHandler?.("link", () => {
    openDialog({ quill, ...getSelectionLinkInfo(quill) });
  });

  const tooltip = quill.theme?.tooltip;
  const originalEdit = tooltip?.edit?.bind(tooltip);

  if (tooltip && originalEdit) {
    tooltip.edit = (mode?: string, preview?: string) => {
      if (mode === "link") {
        const info = getSelectionLinkInfo(quill);
        openDialog({
          quill,
          ...info,
          url: info.url || (typeof preview === "string" ? preview : ""),
        });
        tooltip.hide?.();
        return;
      }
      return originalEdit(mode, preview);
    };
  }

  return () => {
    if (tooltip && originalEdit) {
      tooltip.edit = originalEdit;
    }
  };
};

const QuillLinkDialog: React.FC<QuillLinkDialogProps> = ({ payload, onClose }) => {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const textRef = useRef<HTMLInputElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!payload) return;
    setText(payload.text);
    setUrl(payload.url);
  }, [payload]);

  useEffect(() => {
    if (!payload) return;
    const focusField = payload.text ? urlRef.current : textRef.current;
    const timer = window.setTimeout(() => {
      focusField?.focus();
      focusField?.select();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [payload]);

  const canSave = url.trim().length > 0;

  const restoreEditorFocus = (quill: QuillLike) => {
    window.requestAnimationFrame(() => quill.focus());
  };

  const handleApply = () => {
    if (!payload || !canSave) return;
    applyQuillLink(payload.quill, payload.range, text, url);
    onClose();
    restoreEditorFocus(payload.quill);
  };

  const handleRemove = () => {
    if (!payload) return;
    removeQuillLink(payload.quill, payload.range);
    onClose();
    restoreEditorFocus(payload.quill);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApply();
    }
  };

  return (
    <Modal
      visible={Boolean(payload)}
      title={payload?.isEdit ? "Edit link" : "Add a link"}
      size="medium"
      className="quill-link-dialog"
      closeButton
      closeOnBackDrop
      onClose={onClose}
    >
      <div className="quill-link-dialog__body" onKeyDown={handleKeyDown}>
        <label className="quill-link-dialog__label" htmlFor="quill-link-text">
          Text
        </label>
        <input
          id="quill-link-text"
          ref={textRef}
          className="quill-link-dialog__input"
          type="text"
          value={text}
          placeholder="Enter text to display"
          onChange={(event) => setText(event.target.value)}
        />

        <label className="quill-link-dialog__label" htmlFor="quill-link-url">
          Link
        </label>
        <input
          id="quill-link-url"
          ref={urlRef}
          className="quill-link-dialog__input"
          type="url"
          value={url}
          placeholder="https://www.example.com"
          onChange={(event) => setUrl(event.target.value)}
        />

        <div className="quill-link-dialog__actions">
          {payload?.isEdit ? (
            <button
              type="button"
              className="quill-link-dialog__remove"
              onClick={handleRemove}
            >
              Remove
            </button>
          ) : (
            <span />
          )}
          <div className="quill-link-dialog__buttons">
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              className="quill-link-dialog__cancel"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              color="primary"
              variant="contained"
              rounded
              className="quill-link-dialog__save"
              disabled={!canSave}
              onClick={handleApply}
            >
              {payload?.isEdit ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuillLinkDialog;
